/**
 * ProfileHeader Component
 *
 * Displays user avatar (photo or initials), display name with edit trigger, and email.
 * Used at the top of the unified Profile page.
 *
 * @example
 * ```tsx
 * <ProfileHeader
 *   displayName="John Doe"
 *   email="john@example.com"
 *   photoURL={null}
 *   onEditName={() => setEditModalOpen(true)}
 * />
 * ```
 */

import EditIcon from '@mui/icons-material/Edit';
import { useState, type CSSProperties } from 'react';

interface ProfileHeaderProps {
  /** User's display name */
  displayName: string;
  /** User's email address (read-only) */
  email: string;
  /** URL to user's profile photo, or null to show initials */
  photoURL: string | null;
  /** Callback when edit name button is clicked */
  onEditName: () => void;
}

/**
 * ProfileHeader displays avatar, editable name, and email
 */
export function ProfileHeader({ displayName, email, photoURL, onEditName }: ProfileHeaderProps) {
  const [imageError, setImageError] = useState(false);

  const initials =
    displayName
      .trim()
      .split(' ')
      .filter((n) => n.length > 0)
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || '?';

  return (
    <div style={containerStyle}>
      <div style={avatarStyle}>
        {photoURL && !imageError ? (
          <img
            src={photoURL}
            alt={displayName}
            style={avatarImageStyle}
            onError={() => setImageError(true)}
          />
        ) : (
          <span style={initialsStyle}>{initials}</span>
        )}
      </div>
      <button style={nameRowStyle} onClick={onEditName} aria-label="Edit display name">
        <span style={displayNameStyle}>{displayName}</span>
        <EditIcon sx={editIconStyle} />
      </button>
      <span style={emailStyle}>{email}</span>
    </div>
  );
}

const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: 'var(--spacing-lg)',
  gap: 'var(--spacing-sm)',
};

const avatarStyle: CSSProperties = {
  width: 80,
  height: 80,
  borderRadius: 'var(--radius-full)',
  backgroundColor: 'var(--color-primary)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
};

const avatarImageStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
};

const initialsStyle: CSSProperties = {
  color: 'white',
  fontSize: 'var(--font-size-2xl)',
  fontWeight: 600,
};

const nameRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-xs)',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 'var(--spacing-xs)',
};

const displayNameStyle: CSSProperties = {
  fontSize: 'var(--font-size-xl)',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
};

const editIconStyle = {
  fontSize: 18,
  color: 'var(--color-text-secondary)',
};

const emailStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-secondary)',
};

export default ProfileHeader;
